import { apiFetch } from './apiFetch';

/**
 * Fetch all system banners with pagination, search, and filters
 * @param {Object} queryKey - React Query key with pagination params
 * @returns {Promise<Object>} - List of banners with pagination
 */
export const getSystemBanners = ({ queryKey }) => {
  const [, { page = 1, search, filter, count = 10 }] = queryKey;

  const url = new URL('/api/v2/admin/system_banners', window.location.origin);

  const params = {
    page,
    count,
    ...(search && { search }),
    ...(filter &&
      Object.keys(filter).length > 0 && { filter: JSON.stringify(filter) }),
  };

  url.search = new URLSearchParams(params).toString();

  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
};

/**
 * Fetch a single system banner by ID
 * @param {Object} params - Query params with bannerId
 * @returns {Promise<Object>} - Single banner details
 */
export const getSystemBanner = ({ queryKey }) => {
  const [, { id }] = queryKey;

  return apiFetch({
    url: `/api/v2/admin/system_banners/${id}`,
    method: 'GET',
  });
};

/**
 * Create a new system banner
 * @param {Object} bannerData - Banner data to create
 * @returns {Promise<Object>} - Created banner
 */
export const createSystemBanner = (bannerData) => {
  return apiFetch({
    url: '/api/v2/admin/system_banners',
    method: 'POST',
    body: { system_banner: bannerData },
  });
};

/**
 * Update an existing system banner
 * @param {number} id - Banner ID
 * @param {Object} bannerData - Updated banner data
 * @returns {Promise<Object>} - Updated banner
 */
export const updateSystemBanner = (id, bannerData) => {
  return apiFetch({
    url: `/api/v2/admin/system_banners/${id}`,
    method: 'PATCH',
    body: { system_banner: bannerData },
  });
};

/**
 * Delete a system banner
 * @param {number} id - Banner ID to delete
 * @returns {Promise<Object>} - Deletion confirmation
 */
export const deleteSystemBanner = (id) => {
  return apiFetch({
    url: `/api/v2/admin/system_banners/${id}`,
    method: 'DELETE',
  });
};

/**
 * Fetch currently active banners for a specific application
 * @param {string} application - Application name (e.g., 'alex', 'all')
 * @returns {Promise<Object>} - List of active banners
 */
export const getActiveBanners = (application = 'all') => {
  const url = new URL(
    '/api/v2/admin/system_banners/active',
    window.location.origin
  );
  url.searchParams.set('application', application);

  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
};

/**
 * Banner type options for forms
 */
export const BANNER_TYPES = [
  { value: 'info', label: 'Info', color: 'blue' },
  { value: 'warning', label: 'Warning', color: 'yellow' },
];

/**
 * Application options for forms
 */
export const APPLICATIONS = [
  { value: 'all', label: 'All Applications' },
  { value: 'alex', label: 'ALEX Only' },
  { value: 'admired_leadership', label: 'Admired Leadership Only' },
];

/**
 * Status options for filtering
 */
export const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'expired', label: 'Expired' },
];

